import {
	Card,
	CardHeader,
	CardBody,
	CardDivider,
	withFilters,
	Button,
	FormFileUpload,
	__experimentalHStack as HStack,
	__experimentalVStack as VStack,
} from '@wordpress/components';
import { Fragment, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { useEntityRecords } from '@wordpress/core-data';
import { dateI18n } from '@wordpress/date';
import { downloadBlob } from '@wordpress/blob';
import { useDispatch } from '@wordpress/data';
import { store as noticesStore } from '@wordpress/notices';
import apiFetch from '@wordpress/api-fetch';

const ExportPlaceholder = withFilters( 'popper.settings.tools.export' )( () => {
	return <Fragment />;
} );

const ImportPlaceholder = withFilters( 'popper.settings.tools.import' )( () => {
	return <Fragment />;
} );

const Tools = () => {
	const { records: popups, isResolving: isLoadingPopups } = useEntityRecords(
		'postType',
		'popper',
		{
			_embed: 'wp:term,author',
		}
	);
	const { createNotice } = useDispatch( noticesStore );

	const currentDate = dateI18n( '', new Date() );
	const [ files, setFiles ] = useState( [] );
	const [ loading, setLoading ] = useState( false );

	const exportPopups = ( items ) => {
		const fileContent = JSON.stringify( items, null, 2 );
		const filename = `popper-export-${ currentDate }.json`;

		downloadBlob( filename, fileContent, 'application/json' );
	};

	const importPopups = () => {
		const fileReader = new FileReader();
		fileReader.onloadend = ( e ) => {
			const content = e.target.result;
			try {
				const result = JSON.parse( content );
				importReqs( result );
				return true;
			} catch ( err ) {
				createNotice( 'error', '⚠️ ' + err, {
					type: 'snackbar',
					explicitDismiss: true,
				} );
			}
		};
		fileReader.readAsText( files[ 0 ] );
	};

	const importReqs = ( objs ) => {
		const reqs = { requests: [] };
		for ( const p of objs ) {
			const req = {
				method: 'POST',
				path: '/wp/v2/popper',
				body: {
					title: p.title,
					excerpt: p.excerpt,
					content: p.content,
					status: 'draft',
				},
			};
			reqs.requests.push( req );
		}
		setLoading( true );
		apiFetch( {
			path: '/batch/v1/',
			method: 'POST',
			data: reqs,
		} ).then( () => {
			setLoading( false );
			setFiles( false );
			createNotice(
				'success',
				'🎯 ' + __( 'Popups imported correctly.', 'popper' ),
				{
					type: 'snackbar',
				}
			);
		} );
	};

	return (
		<Fragment>
			<Card>
				<CardHeader size="small">
					<b>{ __( 'Import/Export', 'popper' ) }</b>
				</CardHeader>

				<CardBody>
					<VStack>
						<h2>{ __( 'Export' ) }</h2>
						<p>
							{ __(
								'You can use this tool to export all your popups.',
								'popper'
							) }
						</p>
						<HStack justify={ 'flex-start' }>
							<Button
								onClick={ () => exportPopups( popups ) }
								text={ __( 'Export popups', 'popper' ) }
								variant="primary"
								disabled={ isLoadingPopups }
							/>
						</HStack>
						<ExportPlaceholder
							exportPopups={ exportPopups }
							disabled={ isLoadingPopups }
							popups={ popups }
						/>
						<CardDivider />
						<h2>{ __( 'Import' ) }</h2>
						<p>
							{ __(
								'You can use this tool to import all popup previously exported using this tool.',
								'popper'
							) }
						</p>
						<HStack justify="flex-start">
							<FormFileUpload
								accept="application/json"
								onChange={ ( event ) =>
									setFiles( event.target.files )
								}
								variant="secondary"
							>
								{ files.length
									? files[ 0 ]?.name
									: __( 'Choose file', 'formello' ) }
							</FormFileUpload>
							{ files.length && (
								<Button
									variant="primary"
									text={ __( 'Upload' ) }
									onClick={ importPopups }
									isBusy={ loading }
									disabled={ loading }
								/>
							) }
						</HStack>
						<ImportPlaceholder />
					</VStack>
				</CardBody>
			</Card>
		</Fragment>
	);
};

export default withFilters( 'popper.settings.tools' )( Tools );
